import React, { createContext, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../api/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);
  const [tenantInfo, setTenantInfo] = useState(null);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('active_workspace_id');
    setCurrentUser(null);
    setToken(null);
    setTenantInfo(null);
  };

  // Bootstrap au démarrage: si token existe, récupérer /auth/me ET /tenant/me
  useEffect(() => {
    let cancelled = false;
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const res = await api.get('/auth/me');
        if (cancelled) return;
        setCurrentUser(res.data.user ?? null);
        setToken(storedToken);

        // Récupérer les infos du tenant (type, etc.)
        try {
          const tenantRes = await api.get('/tenant/me');
          if (!cancelled && tenantRes.data.success) {
            setTenantInfo(tenantRes.data.tenant);
          }
        } catch (err) {
          console.error('Erreur récupération tenant:', err);
        }
      } catch (error) {
        if (error?.response?.status === 401) logout();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    checkAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      const { token: newToken, user } = res.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setCurrentUser(user);

      // Récupérer les infos du tenant après login
      try {
        const tenantRes = await api.get('/tenant/me');
        if (tenantRes.data.success) {
          setTenantInfo(tenantRes.data.tenant);
        }
      } catch (err) {
        console.error('Erreur récupération tenant:', err);
      }

      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Identifiants incorrects',
      };
    }
  };

  // Rôles
  const isAdmin = () => currentUser?.role === 'admin';
  const isCollaborator = () =>
    currentUser?.role === 'collaborator' || currentUser?.role === 'collaborateur';
  const hasRole = (role) => currentUser?.role === role;

  // Super admin plateforme (flag permissions.globalSuperAdmin)
  const isSuperAdmin = !!currentUser?.permissions?.globalSuperAdmin;

  // Type de tenant
  // ⚠️ IMPORTANT : on se base sur tenantType (défaut 'cabinet') pour que isCabinet / isCabinetType
  // reste cohérent même si tenantInfo n'est pas encore chargé.
  const tenantType = tenantInfo?.type || 'cabinet';
  const isCabinet = tenantType === 'cabinet' || tenantType === 'cabinet_medical';

  // ✅ Modules activables (niveau tenant - UNIQUEMENT pour les companies)
  // Pour les cabinets (comptable et médical), les features sont gérées par workspace (voir WorkspaceContext).
  // On force donc false au niveau tenant pour ces types, afin de ne jamais
  // exposer une feature à cause d'un ancien flag tenant.
  const accountingEnabled = isCabinet ? false : !!tenantInfo?.entitlements?.accountingEnabled;
  const stockEnabled = isCabinet ? false : !!tenantInfo?.entitlements?.stockEnabled;
  const eSignatureEnabled = isCabinet ? false : !!tenantInfo?.entitlements?.eSignatureEnabled;
  const cabinetManagementEnabled = isCabinet ? false : !!tenantInfo?.entitlements?.cabinetManagementEnabled;

  const value = {
    currentUser,
    token,
    loading,
    login,
    logout,
    isAdmin,
    isCollaborator,
    hasRole,
    isSuperAdmin,
    isAuthenticated: !!currentUser && !!token,
    userRole: currentUser?.role,
    tenantInfo,
    tenantType,
    isCabinet,
    accountingEnabled,
    stockEnabled,
    eSignatureEnabled,
    cabinetManagementEnabled,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
